import {Component, EventEmitter, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {ToastService} from "../../services/Toast.service";
import {SwitchBoard} from "../../services/SwitchBoard";
import {LoadingService} from "../../services/LoadingService";
import {PageTitleService} from "../../services/PageTitle";
import {StateService} from "../../services/StateService";
import {Router} from "@angular/router";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";
import {Session} from "../../services/Session";
import {takeUntil} from "rxjs/operators";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {AddUserService} from "../../tpiq-services/add-user.service";
import {NgxScrollEvent} from 'ngx-scroll-event/dist/ngx-scroll-event';
import {TpiqPaginatedTableComponent} from "../../tables/tpiq-paginated-table/tpiq-paginated-table.component";
import {FormsModule} from "@angular/forms";
import {FoundationInit} from "../../directives/foundation.directive";
import moment from 'moment';
import { isValidEmail } from '../../utils/validators';

declare const _: any;


@Component({
  selector: 'app-manage-users',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqPaginatedTableComponent, FoundationInit],
  templateUrl: './manage-users.component.html',
  styleUrl: './manage-users.component.scss'
})
export class ManageUsersComponent  implements OnInit, OnDestroy {
  readonly isValidEmail = isValidEmail;

  org: any;
  user: string;
  users: Array<any> = [];
  usersTableData: any = {};
  usersSort: any = {};
  isLoading = true;
  nextIndex = 0;
  hasNextSet: boolean;
  hasUsersList: boolean;
  searchString: string;
  destroy$: EventEmitter<void> = new EventEmitter<void>();
  searchData: any = {};
  reveal: any = {
    createOrUpdateUser: false
  };
  usersInfo: any;
  currentPlan: string;
  editMode = false;
  userData: any = {
    countryCode: '+1'
  };
  @Input() isFromOnboard;

  constructor(
    private toastService: ToastService,
    private addUserService: AddUserService,
    private switchBoard: SwitchBoard,
    private loadingService: LoadingService,
    private titleService: PageTitleService,
    private stateService: StateService,
    private _router: Router,
    private taxPlanningService: TaxPlanningService
  ) {
  }

  ngOnInit() {
    this._inIt();
  }

  _inIt() {
    const base = this;
    this.org = Session.getOrg();
    this.user = Session.getUser().id;
    this.getSubscriptionInfo();
    this.getUsersLimitsData();
    base.setUsers();
    this.switchBoard.onPageChangeListener.next({});
    this.switchBoard.onToggleFeedback.next({
      display: true,
      pageName: 'Manage users page'
    });

    this.switchBoard.onClickPrev

      .pipe(takeUntil(this.destroy$)).subscribe(title => {
      this.gotoDefaultPage();
    });
/*
    this.switchBoard.onClearSearch.next({});
    this.switchBoard.onSearchTask
      .pipe(takeUntil(this.destroy$)).subscribe(text => {
      this.searchInUsers(text.searchText);
    });
*/
    this.switchBoard.onToastConfirm
      .pipe(takeUntil(this.destroy$)).subscribe(toast => {
      this.deleteUser(toast.data);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  gotoDefaultPage() {
    const prevState = this.stateService.getPrevState();
    if (prevState.key === 'Tools') {
      this._router.navigate([prevState.url]);
    } else if (prevState) {
      this.stateService.pop();
      this._router.navigate([prevState.url]);
    }
  }

  searchInUsers(searchText) {
    const searchObj = {
      fromIndex : -1,
      searchKey: searchText
    };
    this.searchString = searchText;
    this.searchData = searchObj;
    this.loadingService.triggerLoadingEvent(true);
    this.addUserService.getPaginatedProfileUsers(searchObj)
      .subscribe(response => {
        this.isLoading = false;
        this.users = response.userList;
        this.usersTableData.rows = [];
        this.loadingService.triggerLoadingEvent(false);
        this.buildUsersTableData(this.users, true);
        this.nextIndex += response.userList.length;
        this.hasNextSet = response.hasNextPage;
      }, error => {
        this.isLoading = false;
        this.loadingService.triggerLoadingEvent(false);
      });
  }

  setUsers() {
    this.loadingService.triggerLoadingEvent(true);
    if (!this.isFromOnboard) {
      this.titleService.setPageTitle('Manage Users');
    }
    this.nextIndex = 0;
    this.hasNextSet = false;
    this.usersTableData.rows = [];
    this.resetTabData();
    this.fetchNextSet();
  }

  resetTabData() {
    this.isLoading = true;
    this.usersSort = {};
    this.hasUsersList = false;
  }

  fetchNextSet() {
    if (this.nextIndex === 0 || this.hasNextSet) {
      this.loadingService.triggerLoadingEvent(true);
      const sortObj = this.getTabSortObject();
      let sortingRequest: any = this.getSortingObject(sortObj);
      if (sortingRequest == null) {
        sortingRequest = {
          fromIndex: this.nextIndex
        };
      }
      this.addUserService.getPaginatedProfileUsers(sortingRequest)
        .subscribe(response => {
          this.isLoading = false;
          this.users = response.users;
          this.loadingService.triggerLoadingEvent(false);
          this.buildUsersTableData(response.users, false);
          this.nextIndex += response.users.length;
          this.hasNextSet = false;
        }, error => {
          this.isLoading = false;
          this.loadingService.triggerLoadingEvent(false);
        });
    }
  }

  getTabSortObject() {
    return this.usersSort;
  }

  getSortingObject(sortObject) {
    const base = this;
    for (const key in sortObject) {
      if (sortObject.hasOwnProperty(key) && sortObject[key]) {
        return {
          sortKey: key,
          sortOrder: sortObject[key],
          fromIndex: this.nextIndex
        };
      }
    }
    return null;
  }

  buildUsersTableData(data, refreshTable?) {
    const base = this;
    this.usersTableData.options = {
      height: '64vh',
      selectorString: 'id',
      showSearch: false,
    };
    this.usersTableData.columns = [
      {name: 'firstName', title: 'First Name', type: 'html', sortable: true},
      {name: 'lastName', title: 'Last Name', type: 'normal', sortable: true},
      {name: 'id', title: 'Email', type: 'normal', sortable: true},
      {name: 'lastLoginTime', title: 'Last Logon', type: 'normal', sortable: true},
      {name: 'actions', title: '', type: 'html', sortable: false}
    ];
    this.handleSortOrder(this.usersSort, this.usersTableData.columns);
    let rows = [];
    if (!refreshTable) {
      rows = this.usersTableData.rows || [];
    }
    data.forEach(function(user){
      const row: any = {};
      _.each(Object.keys(user), function(key){
        if (key === 'firstName') {
          row[key] = `<a class='actionOnId' data-action='edit'><span class='icon'>${user[key]}</span></a>`;
        }else if (key === 'lastLoginTime') {
          row[key] = user[key] ? moment(user[key]).format('MM/DD/YYYY h:mm A') : '';
        }else {
          row[key] = user[key];
        }
      });
      row.actions = `<a class='action' data-action='delete' style='margin:0px 0px 0px 5px;'><i class='icon ion-trash-b'></i></a>`;
      rows.push(row);
    });
    this.usersTableData = {...this.usersTableData, rows};
    if (refreshTable) {
      this.hasUsersList = false;
    }
    setTimeout(function(){
      if (base.usersTableData.rows.length > 0) {
        base.isLoading = false;
      } else {
        base.isLoading = false;
      }
      base.hasUsersList = true;
    });
    this.loadingService.triggerLoadingEvent(false);
  }

  handleSortOrder(sortOrder, columns) {
    _.each(columns, function(column){
      column.sortType = sortOrder[column.name];
    });
  }

  handleAction($event) {
    const action = $event.action;
    if (action === 'delete') {
      this.removeUser($event);
    } else if (action === 'edit') {
      this.getUserProfileData($event.id);
    }
  }

  getUserProfileData(user) {
    const base = this;
    this.loadingService.triggerLoadingEvent(true);
    this.addUserService.getUserProfileData(this.org, user)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.userData = response;
        this.editMode = true;
        this.reveal.createOrUpdateUser = true;
      }, error => {
        error = JSON.parse(error);
        const message = error.message ? error.message : 'Failed to get User Details';
        this.toastService.pop(TOAST_TYPE.error, message);
        this.loadingService.triggerLoadingEvent(false);
      });
  }

  handleScroll(event: NgxScrollEvent) {
    const base = this;
    if (event.isReachingBottom && !base.isLoading && !event.isWindowEvent) {
      base.isLoading = true;
      base.fetchNextSet();
    }
  }

  sortByColumn(obj) {
    this.sortInUsers(obj);
  }

  sortInUsers(obj) {
    this.usersSort[obj.column] = obj.order;
    this.resetSortOrder(this.usersSort, this.usersTableData.columns, obj.column);
    this.nextIndex = 0;
    const sortObj = {
      sortKey: obj.column,
      sortOrder: obj.order,
      fromIndex: this.nextIndex,
    };
    this.loadingService.triggerLoadingEvent(true);
    this.addUserService.getPaginatedProfileUsers(sortObj)
      .subscribe(response => {
        this.isLoading = false;
        this.users = response.users;
        this.usersTableData.rows = [];
        this.loadingService.triggerLoadingEvent(false);
        this.buildUsersTableData(this.users, true);
        this.nextIndex += response.users.length;
        this.hasNextSet = false;
      }, error => {
        this.isLoading = false;
        this.loadingService.triggerLoadingEvent(false);
      });
  }

  resetSortOrder(sortOrder, columns, column) {
    _.each(columns, function(col){
      if (col.name !== column) {
        sortOrder[col.name] = '';
      }
    });
  }

  updateUser() {
    this.loadingService.triggerLoadingEvent(true);
    this.addUserService.updateUser(this.userData)
      .subscribe(success  => {
        this.loadingService.triggerLoadingEvent(false);
        this.setUsers();
        this.toastService.pop(TOAST_TYPE.success, 'User updated successfully');
        this.hideReveal('createOrUpdateUser');
      }, error =>  {
        this.loadingService.triggerLoadingEvent(false);
        try{
          const message = error.message ? error.message : 'Failed To Update User';
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (err){
          this.toastService.pop(TOAST_TYPE.error, 'Failed To Update User');
        }
      });
  }

  addNewUser() {
    this.editMode = false;
    this.reveal.createOrUpdateUser = true;
  }

  hideReveal(key){
    this.editMode = false;
    this.userData = {
      countryCode: '+1'
    };
    this.reveal[key] = false;
  }

  removeUser($event) {
    this.toastService.pop(TOAST_TYPE.confirm, 'Are you sure you want to delete?', $event);
  }

  deleteUser(data) {
    this.loadingService.triggerLoadingEvent(true);
    this.addUserService.removeUser(this.org, this.user, data.id)

      .subscribe(success => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'User Deleted Successfully');
        this.hasUsersList = false;
        this.setUsers();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed To Delete User');
      });
  }

  getUsersLimitsData() {
    this.addUserService.getUsersLimits(this.org, this.user)
      .subscribe(response => {
        this.usersInfo = response;
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        try{
          const message = error.message ? error.message : 'Failed to get users limit data';
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (err){
          this.toastService.pop(TOAST_TYPE.error, 'Failed to get users limit data');
        }
      });
  }

  getSubscriptionInfo() {
    this.taxPlanningService.getSubscriptionStatus(this.org, this.user).subscribe(subscription => {
      this.currentPlan = subscription.plan;
    });
  }

  // New changes

  saveUser() {
    if (this.validateUser()) {
      if (this.editMode) {
        this.updateUser();
      } else {
        this.addUser();
      }
    }
  }

  validateUser() {
    let validateUser = true;
    if (!this.userData.firstName) {
      this.toastService.pop(TOAST_TYPE.error, 'First Name is Mandatory');
      validateUser = false;
    } else if (!this.userData.lastName) {
      this.toastService.pop(TOAST_TYPE.error, 'Last Name is Mandatory');
      validateUser = false;
    }else if (!this.userData.id) {
      this.toastService.pop(TOAST_TYPE.error, 'Email is Mandatory');
      validateUser = false;
    }

    return validateUser;
  }

  addUser() {
    let users = [];
    users.push(this.userData);
    this.loadingService.triggerLoadingEvent(true);
    this.addUserService.addUser(users)
      .pipe(takeUntil(this.destroy$)).subscribe(success  => {
      this.loadingService.triggerLoadingEvent(false);
      this.setUsers();
      this.hideReveal('createOrUpdateUser');
      this.toastService.pop(TOAST_TYPE.success, 'User created successfully');
    }, error =>  {
      this.loadingService.triggerLoadingEvent(false);
      try{
        const message = error.message ? error.message : 'Failed to create user';
        this.toastService.pop(TOAST_TYPE.error, message);
      } catch (err){
        this.toastService.pop(TOAST_TYPE.error, 'Failed to create user');
      }
    });
  }

}
